import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProductStats } from '../services/productService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProductStatsDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('thisMonth'); // Cambiar por defecto a thisMonth
  const [groupBy, setGroupBy] = useState('day'); // day, week, month, year
  
  // Estados para fechas personalizadas
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Función para calcular el rango de fechas según el filtro seleccionado
  const calculateDateRange = (timeRange, customStartDate, customEndDate) => {
    // Crear fechas en la zona horaria de Madrid
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: customStartDate,
        endDate: customEndDate
      };
    }

    switch (timeRange) {
      case 'lastWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 7);
        endDate = new Date(endDate);
        break;
      case 'lastMonth':
        startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - 1);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setDate(0); // Último día del mes anterior
        break;
      case 'lastYear':
        startDate = new Date(endDate);
        startDate.setFullYear(endDate.getFullYear() - 1);
        startDate.setMonth(0);
        startDate.setDate(1);
        endDate = new Date(endDate);
        endDate.setFullYear(endDate.getFullYear() - 1);
        endDate.setMonth(11);
        endDate.setDate(31);
        break;
      case 'last7days':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90days':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'thisWeek':
        startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - endDate.getDay() + 1); // Lunes de esta semana
        endDate = new Date(endDate);
        endDate.setDate(endDate.getDate() - endDate.getDay() + 7); // Domingo de esta semana
        break;
      case 'thisMonth':
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(endDate.getFullYear(), 0, 1);
        endDate = new Date(endDate.getFullYear(), 11, 31);
        break;
      // Mantener compatibilidad con valores anteriores
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case 'year':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
        break;
    }

    // Ajustar las horas para el inicio y fin del día
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Formatear las fechas en YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  // Función para procesar los valores de ingresos
  const processRevenueValue = (value) => {
    if (typeof value === 'string') {
      // Convertir el string a número decimal
      return parseFloat(value) || 0;
    }
    return parseFloat(value) || 0;
  };

  // Función para formatear fechas según el agrupamiento
  const formatDate = (date, groupBy) => {
    const d = new Date(date);
    switch (groupBy) {
      case 'day':
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
      case 'week':
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${weekStart.getDate()}/${weekStart.getMonth() + 1} - ${weekEnd.getDate()}/${weekEnd.getMonth() + 1}`;
      case 'month':
        return d.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      case 'year':
        return d.getFullYear().toString();
      default:
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    }
  };

  // Función para obtener la clave de agrupación según el período
  const getGroupKey = (date, groupBy) => {
    const d = new Date(date);
    switch (groupBy) {
      case 'day':
        return d.toISOString().split('T')[0];
      case 'week':
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        return weekStart.toISOString().split('T')[0];
      case 'month':
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      case 'year':
        return d.getFullYear().toString();
      default:
        return d.toISOString().split('T')[0];
    }
  };

  // Función para obtener todas las fechas en un rango
  const getAllDatesInRange = (startDate, endDate, groupBy) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      dates.push(getGroupKey(currentDate.toISOString(), groupBy));
      switch (groupBy) {
        case 'day':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'week':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'month':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'year':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
      }
    }
    return dates;
  };

  // Función para agrupar ventas por período
  const groupSalesByPeriod = (sales, groupBy) => {
    if (!sales || sales.length === 0) return [];

    // Obtener la fecha más antigua y más reciente
    const dates = sales.map(sale => new Date(sale.date));
    const startDate = new Date(Math.min(...dates));
    const endDate = new Date(Math.max(...dates));

    // Obtener todas las fechas en el rango
    const allDates = getAllDatesInRange(startDate, endDate, groupBy);
    
    // Crear el objeto agrupado con todas las fechas inicializadas
    const grouped = {};
    allDates.forEach(date => {
      grouped[date] = {
        date: date,
        sales: 0,
        revenue: 0,
        pending: 0,
        approved: 0,
        rejected: 0
      };
    });
    
    // Agregar las ventas a sus respectivas fechas
    sales.forEach(sale => {
      const groupKey = getGroupKey(sale.date, groupBy);
      if (grouped[groupKey]) {
        grouped[groupKey].sales += 1;
        
        // Solo sumar ingresos de ventas que no estén rechazadas y tengan comisión > 0
        if (sale.status !== 'rejected') {
          const commission = processRevenueValue(sale.commissionEUR);
          if (commission > 0) {
            grouped[groupKey].revenue += commission;
          }
        }
        
        if (sale.status === 'pending') {
          grouped[groupKey].pending += 1;
        } else if (sale.status === 'approved') {
          grouped[groupKey].approved += 1;
        } else {
          grouped[groupKey].rejected += 1;
        }
      }
    });

    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Calcular las fechas según el rango de tiempo seleccionado
        const dateRange = calculateDateRange(timeRange, customStartDate, customEndDate);
        
        // Llamar al servicio con los parámetros correctos
        const data = await getProductStats(
          id, 
          timeRange, 
          dateRange.startDate, 
          dateRange.endDate
        );
        
        if (data) {
          // Procesar los datos recibidos
          data.totalRevenue = processRevenueValue(data.totalRevenue);
          
          // Agrupar las conversiones por el período seleccionado
          if (data.recentConversions) {
            data.salesByDay = groupSalesByPeriod(data.recentConversions, groupBy);
          }
          
          // Calcular ingresos por día
          if (data.salesByDay) {
            data.revenueByDay = data.salesByDay.map(day => ({
              date: day.date,
              revenue: day.revenue
            }));
          }
          
          // Calcular comisión media de todas las ventas no rechazadas y con comisión > 0
          const nonRejectedSales = data.recentConversions.filter(sale => sale.status !== 'rejected');
          const validSalesForAverage = nonRejectedSales.filter(sale => {
            const commission = processRevenueValue(sale.commissionEUR);
            return commission > 0;
          });
          
          let totalCommissions = 0;
          
          validSalesForAverage.forEach(sale => {
            const commission = processRevenueValue(sale.commissionEUR);
            totalCommissions += commission;
          });
          
          // Calcular totales por agrupación
          if (data.salesByDay) {
            data.totalSales = data.salesByDay.reduce((sum, day) => sum + day.sales, 0);
            data.totalRevenue = data.salesByDay.reduce((sum, day) => sum + day.revenue, 0);
            
            data.pendingSales = data.salesByDay.reduce((sum, day) => sum + day.pending, 0);
            data.approvedSales = data.salesByDay.reduce((sum, day) => sum + day.approved, 0);
            data.rejectedSales = data.salesByDay.reduce((sum, day) => sum + day.rejected, 0);
          }
          
          // Calcular comisión media (usando método corregido)
          if (validSalesForAverage.length > 0) {
            data.averageValue = totalCommissions / validSalesForAverage.length;
          }
          
          // Calcular comisión media de conversiones de Google Ads
          const conversionsWithAds = data.recentConversions.filter(c => c.conversion && c.status !== 'rejected');
          const validGoogleAdsConversions = conversionsWithAds.filter(sale => {
            const commission = processRevenueValue(sale.commissionEUR);
            return commission > 0;
          });
          
          if (validGoogleAdsConversions.length > 0) {
            let totalGoogleAdsCommissions = 0;
            
            validGoogleAdsConversions.forEach(sale => {
              const commission = processRevenueValue(sale.commissionEUR);
              totalGoogleAdsCommissions += commission;
            });
            
            data.averageGoogleAdsValue = totalGoogleAdsCommissions / validGoogleAdsConversions.length;
          }
        }
        
        setStats(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Error al cargar las estadísticas');
        setLoading(false);
      }
    };

    fetchStats();
  }, [id, timeRange, groupBy, customStartDate, customEndDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="alert alert-warning">
        <span>No hay estadísticas disponibles</span>
      </div>
    );
  }

  // Configuración del gráfico de ventas
  const salesChartData = {
    labels: stats.salesByDay.map(item => formatDate(item.date, groupBy)),
    datasets: [
      {
        label: 'Ventas',
        data: stats.salesByDay.map(item => item.sales),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.1
      }
    ]
  };

  const salesChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Ventas por Día'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Número de Ventas'
        }
      }
    }
  };

  // Configuración del gráfico de ingresos
  const revenueChartData = {
    labels: stats.revenueByDay.map(item => formatDate(item.date, groupBy)),
    datasets: [
      {
        label: 'Ingresos',
        data: stats.revenueByDay.map(item => processRevenueValue(item.revenue)),
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 1
      }
    ]
  };

  const revenueChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Ingresos por Día'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatter.format(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Ingresos (€)'
        },
        ticks: {
          callback: function(value) {
            return formatter.format(value);
          }
        }
      }
    }
  };

  const formatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR'
  });

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <h1 className="page-title">Estadísticas del Producto</h1>
          {stats && (
            <div className="page-subtitle mt-2">
              <span className="font-semibold text-base-content">{stats.productName}</span>
              <span className="mx-2">•</span>
              <span>{stats.platformName}</span>
            </div>
          )}
        </div>
        <div className="page-actions">
          <select 
            className="select select-bordered"
            value={timeRange}
            onChange={(e) => {
              const newTimeRange = e.target.value;
              setTimeRange(newTimeRange);
              
              // Si no es rango personalizado, limpiar fechas personalizadas
              if (newTimeRange !== 'custom') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}
          >
            <option value="lastWeek">Semana Anterior</option>
            <option value="lastMonth">Mes Anterior</option>
            <option value="lastYear">Año Anterior</option>
            <option value="last7days">Últimos 7 días</option>
            <option value="last30days">Últimos 30 días</option>
            <option value="last90days">Últimos 90 días</option>
            <option value="thisWeek">Semana Actual</option>
            <option value="thisMonth">Mes Actual</option>
            <option value="thisYear">Año Actual</option>
            <option value="custom">Rango Personalizado</option>
          </select>
          <button 
            onClick={() => navigate(`/products/${id}`)}
            className="btn btn-outline"
          >
            Volver al Producto
          </button>
        </div>
      </div>

      {/* Campos de fecha personalizada */}
      {timeRange === 'custom' && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Rango Personalizado</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-medium">Fecha Inicio</span>
                </label>
                <input
                  type="date"
                  className="input input-bordered w-full"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-medium">Fecha Fin</span>
                </label>
                <input
                  type="date"
                  className="input input-bordered w-full"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resumen de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Ventas Totales</div>
          <div className="stat-value">{stats.totalSales}</div>
          <div className="stat-desc">En el período seleccionado</div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Comisiones Totales</div>
          <div className="stat-value">{formatter.format(stats.totalRevenue)}</div>
          <div className="stat-desc">Excluye ventas rechazadas</div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Estado de Ventas</div>
          <div className="stat-value text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-success">✅ {stats.approvedSales} Aprobadas</span>
              <span className="text-warning">⏳ {stats.pendingSales} Pendientes</span>
              <span className="text-error">❌ {stats.rejectedSales} Rechazadas</span>
            </div>
          </div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Comisión Media</div>
          <div className="stat-value text-sm">
            <div className="flex flex-col gap-1">
              <span>{formatter.format(stats.averageValue)}</span>
              <span className="text-primary">
                {stats.averageGoogleAdsValue ? (
                  formatter.format(stats.averageGoogleAdsValue)
                ) : (
                  '-'
                )}
                <span className="text-xs ml-1">(con conversión)</span>
              </span>
            </div>
          </div>
          <div className="stat-desc">Excluye ventas rechazadas</div>
        </div>
        <div className="stat bg-base-100 shadow rounded-lg">
          <div className="stat-title">Conversiones Google Ads</div>
          <div className="stat-value">{stats.recentConversions.filter(c => c.conversion).length}</div>
          <div className="stat-desc">Ventas con conversión</div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selector de agrupación temporal */}
        <div className="lg:col-span-2 flex justify-end mb-2">
          <select 
            className="select select-bordered select-sm"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="day">Por Día</option>
            <option value="week">Por Semana</option>
            <option value="month">Por Mes</option>
            <option value="year">Por Año</option>
          </select>
        </div>

        {/* Gráfico de ventas */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Ventas por {groupBy === 'day' ? 'Día' : 
              groupBy === 'week' ? 'Semana' : 
              groupBy === 'month' ? 'Mes' : 'Año'}</h2>
            <div className="h-80">
              <Line data={salesChartData} options={salesChartOptions} />
            </div>
          </div>
        </div>

        {/* Gráfico de ingresos */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Comisiones por {groupBy === 'day' ? 'Día' : 
              groupBy === 'week' ? 'Semana' : 
              groupBy === 'month' ? 'Mes' : 'Año'}</h2>
            <div className="h-80">
              <Bar data={revenueChartData} options={revenueChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de ventas recientes */}
      <div className="card bg-base-100 shadow-xl mt-6">
        <div className="card-body">
          <h2 className="card-title">Ventas Recientes</h2>
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Valor</th>
                  <th>Comisión</th>
                  <th>Estado</th>
                  <th>Google Ads</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentConversions.map((sale, index) => (
                  <tr key={index}>
                    <td>
                      <Link 
                        to={`/sales/${sale.id}`}
                        className="link link-primary"
                      >
                        #{sale.id}
                      </Link>
                    </td>
                    <td>{new Date(sale.date).toLocaleDateString()}</td>
                    <td>{formatter.format(sale.valueEUR)}</td>
                    <td>{formatter.format(sale.commissionEUR)}</td>
                    <td>
                      <span className={`badge ${
                        sale.status === 'approved' ? 'badge-success' : 
                        sale.status === 'pending' ? 'badge-warning' : 
                        'badge-error'
                      }`}>
                        {sale.status === 'approved' ? 'Aprobada' : 
                         sale.status === 'pending' ? 'Pendiente' : 
                         'Cancelada'}
                      </span>
                    </td>
                    <td>
                      {sale.conversion ? (
                        <a 
                          href={`https://ads.google.com/aw/conversions?conversionId=${sale.conversion.conversionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link link-primary"
                        >
                          Ver conversión
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductStatsDetailScreen; 
